import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { AlertTriangle, Archive, ArrowLeft, Ban, CheckCircle2, ChevronDown, ChevronUp, Clock, MoreHorizontal, Plus, RefreshCw, Search, ServerCog, ShieldCheck, X } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Badge, Button, Card } from '@/components/ui'
import { CrmSourceAccountsPanel } from '@/components/crm/CrmSourceAccountsPanel'
import { cn } from '@/lib/utils'
import {
  applyCrmAccountTransferCase,
  captureCrmPaymentComparison,
  createCrmAccountTransferCase,
  fetchCrmAccountTransferCases,
  fetchCrmAccountTransferPartnerOptions,
  fetchCrmAccountTransferRebuildScope,
  fetchCrmAccountTransferStudioOptions,
  fetchLatestCrmPaymentComparison,
  rebuildCrmAccountTransferCaseWindow,
  updateCrmAccountTransferCaseStatus,
  type CrmAccountTransferAction,
  type CrmAccountTransferPartnerOption,
  type CrmAccountTransferCase,
  type CrmAccountTransferModel,
  type CrmAccountTransferRebuildScope,
  type CrmAccountTransferStudioOption,
  type CrmAccountTransferStatus,
  type CrmPaymentBookingState,
  type CrmPaymentComparison,
  type CrmPaymentComparisonDay,
  type CrmPaymentComparisonStatus,
} from '@/services/crmAccountTransferService'
import { useAuthStore } from '@/store'

const STATUS_LABELS: Record<CrmAccountTransferStatus, string> = {
  detected: 'Найдено',
  needs_crm_action: 'CRM-действие',
  needs_confirmation: 'Проверка',
  verified: 'Проверено',
  closed: 'Закрыто',
  postponed: 'Отложено',
  dismissed: 'Не актуально',
}

const ACTIVE_STATUSES = new Set<CrmAccountTransferStatus>([
  'detected',
  'needs_crm_action',
  'needs_confirmation',
  'postponed',
  'verified',
])

type CaseListFilter = 'active' | 'finished' | 'all'

const CASE_LIST_FILTERS: Array<{ value: CaseListFilter; label: string }> = [
  { value: 'active', label: 'В работе' },
  { value: 'finished', label: 'Завершённые' },
  { value: 'all', label: 'Все' },
]

const TRANSFER_MODEL_LABELS: Record<CrmAccountTransferModel, string> = {
  new_account: 'Переход на другой CRM-аккаунт',
  same_account_rename: 'Смена владельца в текущем CRM-аккаунте',
}

type ManualFormState = {
  studioInternalId: string
  transitionDate: string
  transferModel: CrmAccountTransferModel
  oldAccount: string
  proposedAccount: string
  oldPartnerNumber: string
  newPartnerNumber: string
  note: string
}

function emptyManualForm(): ManualFormState {
  return {
    studioInternalId: '',
    transitionDate: new Date().toISOString().slice(0, 10),
    transferModel: 'new_account',
    oldAccount: '',
    proposedAccount: '',
    oldPartnerNumber: '',
    newPartnerNumber: '',
    note: '',
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function previousDate(value: string): string {
  const date = new Date(`${value}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() - 1)
  return date.toISOString().slice(0, 10)
}

function partnerLabel(name: string | null | undefined, number: number | null | undefined): string {
  const cleanName = name?.trim()
  if (cleanName) return number ? `${cleanName} (${number})` : cleanName
  return number ? `Франчайзи ${number}` : '-'
}

function accountLabel(value: string | null): string {
  return value?.trim() || '-'
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function studioOptionLabel(row: CrmAccountTransferStudioOption): string {
  return [
    `#${row.studio_number}`,
    row.city_name,
    row.address,
    row.current_account ? `CRM ${row.current_account}` : 'CRM не привязан',
  ].filter(Boolean).join(' · ')
}

function partnerOptionLabel(row: CrmAccountTransferPartnerOption): string {
  return [
    `№${row.partner_number}`,
    row.persons_fio || 'ФИО не указано',
    row.cities_text,
  ].filter(Boolean).join(' · ')
}

function SearchablePartnerSelect({
  label,
  value,
  query,
  options,
  onQueryChange,
  onChange,
}: {
  label: string
  value: string
  query: string
  options: CrmAccountTransferPartnerOption[]
  onQueryChange: (value: string) => void
  onChange: (value: string) => void
}) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/ё/g, 'е')
    const matching = q
      ? options.filter(row => partnerOptionLabel(row).toLowerCase().replace(/ё/g, 'е').includes(q))
      : options
    const rows = matching.slice(0, 60)
    const selected = options.find(row => String(row.partner_number) === value)
    return selected && !rows.some(row => row.partner_number === selected.partner_number)
      ? [selected, ...rows.slice(0, 59)]
      : rows
  }, [options, query, value])

  return (
    <label className="block text-xs font-medium text-muted-foreground">
      {label}
      <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          placeholder="номер, ФИО или город"
          className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
        />
      </div>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        size={5}
        className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
      >
        <option value="">Не выбран</option>
        {filtered.map(row => (
          <option key={row.partner_number} value={row.partner_number}>
            {partnerOptionLabel(row)}
          </option>
        ))}
      </select>
    </label>
  )
}

function matchesSearch(row: CrmAccountTransferCase, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    row.id,
    row.studio_number,
    row.city_name,
    row.address,
    row.old_account,
    row.proposed_account,
    row.old_partner_number,
    row.new_partner_number,
    row.old_persons_fio,
    row.new_persons_fio,
    row.suspicious_studio_number,
    row.status,
    row.detection_kind,
    row.operator_note,
  ]
    .filter(value => value !== null && value !== undefined)
    .join(' ')
    .toLowerCase()
    .includes(q)
}

function matchesCaseListFilter(row: CrmAccountTransferCase, filter: CaseListFilter): boolean {
  if (filter === 'finished') return row.status === 'closed' || row.status === 'dismissed'
  if (filter === 'active') return row.status !== 'closed' && row.status !== 'dismissed'
  return true
}

function statusTone(status: CrmAccountTransferStatus): string {
  if (status === 'needs_crm_action') return 'bg-red-50 text-red-700 border-red-200'
  if (status === 'needs_confirmation') return 'bg-amber-50 text-amber-700 border-amber-200'
  if (status === 'verified') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (status === 'postponed') return 'bg-slate-50 text-slate-700 border-slate-200'
  if (status === 'dismissed') return 'bg-zinc-50 text-zinc-500 border-zinc-200'
  return 'bg-sky-50 text-sky-700 border-sky-200'
}

const COMPARISON_LABELS: Record<CrmPaymentComparisonStatus, string> = {
  missing_accounts: 'Не указаны оба аккаунта',
  same_account: 'Один и тот же аккаунт',
  no_payments: 'Платежей нет',
  exact_duplicate: 'Полное совпадение — риск дубля',
  same_total_different_payments: 'Суммы совпадают, состав отличается',
  partial_overlap: 'Частичное совпадение — риск дубля',
  only_old: 'Платежи только в старом аккаунте',
  only_new: 'Платежи только в новом аккаунте',
  different_totals: 'Суммы расходятся',
}

const BOOKING_LABELS: Record<CrmPaymentBookingState, string> = {
  not_booked: 'В выручку не попало',
  booked_old: 'В выручке только старый',
  booked_new: 'В выручке только новый',
  booked_both: 'В выручке оба — задвоено',
  booked_other: 'В выручке другая сумма',
}

function bookingTone(state: CrmPaymentBookingState): string {
  if (state === 'booked_both') return 'border-red-200 bg-red-50 text-red-700'
  if (state === 'booked_other') return 'border-amber-200 bg-amber-50 text-amber-700'
  if (state === 'not_booked') return 'border-border bg-muted/40 text-muted-foreground'
  return 'border-emerald-200 bg-emerald-50 text-emerald-700'
}

function comparisonTone(status: CrmPaymentComparisonStatus): string {
  if (status === 'exact_duplicate' || status === 'partial_overlap') {
    return 'border-red-200 bg-red-50 text-red-700'
  }
  if (status === 'same_total_different_payments' || status === 'different_totals' || status === 'only_old') {
    return 'border-amber-200 bg-amber-50 text-amber-700'
  }
  if (status === 'only_new') return 'border-sky-200 bg-sky-50 text-sky-700'
  if (status === 'no_payments' || status === 'same_account') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
  return 'border-border bg-muted/40 text-muted-foreground'
}

function formatAmount(value: number, currency: string): string {
  const amount = Number(value ?? 0)
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`
  }
}

function comparisonTotalsWarning(comparison: CrmPaymentComparison): string {
  if (comparison.currencies.length === 0) return 'За выбранный период платежи для сравнения не найдены.'
  return comparison.currencies.map(total => [
    `${total.currency}:`,
    `старый аккаунт ${formatAmount(total.old_amount, total.currency)} (${total.old_count} плат.)`,
    `новый аккаунт ${formatAmount(total.new_amount, total.currency)} (${total.new_count} плат.)`,
    `расхождение ${formatAmount(total.amount_delta, total.currency)}`,
  ].join(' ')).join('\n')
}

function comparisonHasHistoryRisk(comparison: CrmPaymentComparison): boolean {
  return !['same_account', 'no_payments'].includes(comparison.status)
}

// Диапазон пересборки — единственный текст, объясняющий, какие дни выручки
// тронет Apply. Считает его сервер: у UI своей формулы окна нет и быть не должно.
function rebuildScopeLines(scope: CrmAccountTransferRebuildScope): string[] {
  if (!scope.has_window) {
    return ['Пересобирать нечего: платежей этой студии в реестре нет.']
  }

  const lines = [
    `Будет пересобрана выручка за ${formatDate(scope.window_start)} — ${formatDate(scope.window_end)} (${scope.window_days} дн.).`,
  ]

  if (scope.days_before_rolling35 > 0) {
    lines.push(
      `Из них ${scope.days_before_rolling35} дн. старше 35-дневного окна синка — раньше они оставались непересобранными.`
    )
  }

  if (scope.uncovered_before) {
    lines.push(
      `Даты до ${formatDate(scope.uncovered_before)} реестром платежей не покрыты: агрегат за них не пересобирается.`
    )
  }

  return lines
}

function RebuildScopeNote({
  scope,
  loading,
  error,
}: {
  scope: CrmAccountTransferRebuildScope | null
  loading: boolean
  error: string | null
}) {
  if (loading) {
    return (
      <div className="mt-2 text-xs text-muted-foreground">Считаем диапазон пересборки выручки…</div>
    )
  }

  if (error) {
    return <div className="mt-2 text-xs text-red-600">{error}</div>
  }

  if (!scope) return null

  const lines = rebuildScopeLines(scope)

  return (
    <div className="mt-2 rounded border border-border bg-muted/30 px-2 py-1.5 text-xs">
      <div className="font-medium text-foreground">{lines[0]}</div>
      {scope.has_window && (
        <div className="text-muted-foreground">
          Окно считается от даты перехода и затронутых платежей ({scope.affected_rows} строк реестра).
        </div>
      )}
      {lines.slice(1).map(line => (
        <div key={line} className="mt-0.5 text-amber-700">
          {line}
        </div>
      ))}
    </div>
  )
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const COMPARISON_STALE_AFTER_MS = 60 * 60 * 1000

// Сверка — снимок: экран показывает сохраненный расчет, а платежи в него приезжают
// ночным синком Листка. Через час снимок уже мог разойтись с CRM.
function isComparisonStale(capturedAt: string | null | undefined): boolean {
  if (!capturedAt) return false
  const captured = new Date(capturedAt).getTime()
  if (!Number.isFinite(captured)) return false
  return Date.now() - captured > COMPARISON_STALE_AFTER_MS
}

function CaseActionButton({
  children,
  icon,
  tone = 'secondary',
  className,
  ...props
}: {
  children: ReactNode
  icon?: ReactNode
  tone?: 'primary' | 'secondary' | 'menu'
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:pointer-events-none disabled:opacity-50',
        tone === 'primary' && 'bg-primary text-primary-foreground hover:bg-primary/90',
        tone === 'secondary' && 'border border-border bg-background text-foreground hover:bg-muted/60',
        tone === 'menu' && 'w-full justify-start rounded px-2 py-1.5 text-left text-xs hover:bg-muted/70',
        className
      )}
      {...props}
    >
      {icon}
      <span>{children}</span>
    </button>
  )
}

function PaymentComparisonPanel({
  comparison,
  loading,
  error,
  onRefresh,
}: {
  comparison?: CrmPaymentComparison
  loading: boolean
  error: string | null
  onRefresh: () => void
}) {
  const [expandedDay, setExpandedDay] = useState<string | null>(null)
  const capturedAt = comparison?.captured_at ?? comparison?.generated_at ?? null
  const isStale = isComparisonStale(capturedAt)
  const mainCurrency = comparison?.currencies[0]?.currency ?? 'RUB'

  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/20 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="text-sm font-semibold">Сверка платежей за 35 дней</div>
        <div className="flex flex-col items-end gap-1">
          <CaseActionButton
            onClick={onRefresh}
            disabled={loading}
            icon={<RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />}
          >
            Обновить
          </CaseActionButton>
          {capturedAt && (
            <div className="text-right text-[11px] text-muted-foreground">
              Пересчитано {formatDateTime(capturedAt)}
            </div>
          )}
          {isStale && (
            <div className="text-right text-[11px] font-medium text-amber-700">
              Данные могли устареть — обновите сверку с Листком
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {loading && !comparison && (
        <div className="mt-3 text-xs text-muted-foreground">Сверяем платежи двух аккаунтов…</div>
      )}

      {!loading && !comparison && !error && (
        <div className="mt-3 text-xs text-muted-foreground">Сохраненной сверки пока нет.</div>
      )}

      {comparison && (
        <div className="mt-3 space-y-3">
          {comparison.has_booked_duplicate ? (
            <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
              Задвоено: в выручку уже попали оба набора
              <div className="mt-1 text-xs font-normal">
                По студии учтено {formatAmount(comparison.ledger_total, mainCurrency)} — это старый и новый
                аккаунт вместе. Дубль уже в отчётах, разбирать нужно его, а не предотвращать.
                {comparison.foreign_flagged_total > 0 && (
                  <> Строк, помеченных чужими: {formatAmount(comparison.foreign_flagged_total, mainCurrency)}
                    {' '}— фильтр по ним ещё не включён.</>
                )}
              </div>
            </div>
          ) : (
            <div className={`rounded border px-3 py-2 text-sm font-medium ${comparisonTone(comparison.status)}`}>
              {COMPARISON_LABELS[comparison.status]}
              {comparison.has_overlap && (
                <div className="mt-1 text-xs font-normal">
                  Не меняйте временный номер на реальный, пока совпадающая история не разобрана.
                </div>
              )}
            </div>
          )}

          <div className="text-[11px] text-muted-foreground">
            {comparison.old_account ?? 'старый аккаунт не указан'} → {comparison.new_account ?? 'новый аккаунт не указан'} ·{' '}
            студия {comparison.studio_number}
            {comparison.suspicious_studio_number !== null ? ` / временный номер ${comparison.suspicious_studio_number}` : ''} ·{' '}
            {formatDate(comparison.window_start)}–{formatDate(comparison.window_end)}
          </div>

          {(comparison.detected_studio_numbers?.length ?? 0) > 0 && (
            <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              <div className="font-medium">Найдены платежи под номерами вне справочника студий</div>
              <div className="mt-1 space-y-0.5">
                {comparison.detected_studio_numbers?.map(item => (
                  <div key={item.studio_number}>
                    номер {item.studio_number}: {item.payment_count} плат. на{' '}
                    {Number(item.amount ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ·{' '}
                    {formatDate(item.first_date)}–{formatDate(item.last_date)}
                  </div>
                ))}
              </div>
              <div className="mt-1">
                Система включила их в сверку как историю нового аккаунта — вводить номер вручную не нужно.
              </div>
            </div>
          )}

          {comparison.currencies.length > 0 && (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {comparison.currencies.map(total => (
                <div key={total.currency} className="rounded border border-border bg-background p-2 text-xs">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="font-semibold">{total.currency}</span>
                    <span className={`rounded border px-1.5 py-0.5 text-[10px] ${comparisonTone(total.status)}`}>
                      {COMPARISON_LABELS[total.status]}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-muted-foreground">Старый · {total.old_count} плат.</div>
                      <div className="font-medium">{formatAmount(total.old_amount, total.currency)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Новый · {total.new_count} плат.</div>
                      <div className="font-medium">{formatAmount(total.new_amount, total.currency)}</div>
                    </div>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    Совпало: {total.matched_count} · только старый: {total.old_only_count} · только новый: {total.new_only_count}
                  </div>
                  <div className="mt-1 font-medium">Разница: {formatAmount(total.amount_delta, total.currency)}</div>
                  <div className="mt-2 border-t border-border pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">В выручке студии</span>
                      <span className={`rounded border px-1.5 py-0.5 text-[10px] ${bookingTone(total.booking_state)}`}>
                        {BOOKING_LABELS[total.booking_state]}
                      </span>
                    </div>
                    <div className="font-medium">
                      {total.ledger_amount === null ? '—' : formatAmount(total.ledger_amount, total.currency)}
                    </div>
                    {total.foreign_flagged_count > 0 && (
                      <div className="mt-0.5 text-muted-foreground">
                        Помечено чужими: {total.foreign_flagged_count} стр. ·{' '}
                        {formatAmount(total.foreign_flagged_amount, total.currency)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {comparison.daily.length > 0 && (
            <div className="overflow-x-auto rounded border border-border bg-background">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-2 py-2 font-medium">Дата</th>
                    <th className="px-2 py-2 font-medium">Старый</th>
                    <th className="px-2 py-2 font-medium">Новый</th>
                    <th className="px-2 py-2 font-medium">Совпало</th>
                    <th className="px-2 py-2 font-medium">Разница</th>
                    <th className="px-2 py-2 font-medium">В выручке</th>
                    <th className="px-2 py-2 font-medium">Результат</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.daily.map(day => {
                    const dayKey = `${day.payment_date}:${day.currency}`
                    const expanded = expandedDay === dayKey
                    const pairs = comparison.payments.filter(
                      payment => payment.payment_date === day.payment_date && payment.currency === day.currency
                    )
                    return (
                      <DayComparisonRows
                        key={dayKey}
                        day={day}
                        expanded={expanded}
                        pairs={pairs}
                        onToggle={() => setExpandedDay(expanded ? null : dayKey)}
                      />
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DayComparisonRows({
  day,
  expanded,
  pairs,
  onToggle,
}: {
  day: CrmPaymentComparisonDay
  expanded: boolean
  pairs: CrmPaymentComparison['payments']
  onToggle: () => void
}) {
  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="whitespace-nowrap px-2 py-2">
          <button type="button" className="inline-flex items-center gap-1 font-medium" onClick={onToggle}>
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {formatDate(day.payment_date)}
          </button>
        </td>
        <td className="whitespace-nowrap px-2 py-2">
          {day.old_count} · {formatAmount(day.old_amount, day.currency)}
        </td>
        <td className="whitespace-nowrap px-2 py-2">
          {day.new_count} · {formatAmount(day.new_amount, day.currency)}
        </td>
        <td className="px-2 py-2">{day.matched_count}</td>
        <td className="whitespace-nowrap px-2 py-2">{formatAmount(day.amount_delta, day.currency)}</td>
        <td className="whitespace-nowrap px-2 py-2">
          <div>{day.ledger_amount === null ? '—' : formatAmount(day.ledger_amount, day.currency)}</div>
          <span className={`mt-0.5 inline-block rounded border px-1.5 py-0.5 text-[10px] ${bookingTone(day.booking_state)}`}>
            {BOOKING_LABELS[day.booking_state]}
          </span>
        </td>
        <td className="px-2 py-2">
          <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] ${comparisonTone(day.status)}`}>
            {COMPARISON_LABELS[day.status]}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={7} className="p-2">
            <div className="space-y-1">
              {pairs.map((pair, index) => (
                <div
                  key={`${pair.match_status}:${pair.old_payment?.payment_id ?? '-'}:${pair.new_payment?.payment_id ?? '-'}:${index}`}
                  className="grid grid-cols-1 gap-1 rounded border border-border bg-background p-2 md:grid-cols-[110px_1fr_1fr]"
                >
                  <div className="text-[11px] font-medium text-muted-foreground">
                    {pair.match_status === 'matched' ? 'Совпало' : pair.match_status === 'old_only' ? 'Только старый' : 'Только новый'}
                  </div>
                  <PaymentSide label="Старый" payment={pair.old_payment} currency={pair.currency} />
                  <PaymentSide label="Новый" payment={pair.new_payment} currency={pair.currency} />
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function PaymentSide({
  label,
  payment,
  currency,
}: {
  label: string
  payment: CrmPaymentComparison['payments'][number]['old_payment']
  currency: string
}) {
  if (!payment) return <div className="text-[11px] text-muted-foreground">{label}: —</div>
  return (
    <div className="min-w-0 text-[11px]">
      <div className="font-medium">
        {label}: {formatAmount(payment.amount, currency)} · {formatDateTime(payment.paid_at)}
      </div>
      <div className="truncate text-muted-foreground">
        ID {payment.payment_id} · студия {payment.studio_number ?? 'без номера'} · {payment.payment_method}
      </div>
    </div>
  )
}

function CaseRow({
  row,
  applyingId,
  onApply,
  onAction,
  onComplete,
  onRebuild,
}: {
  row: CrmAccountTransferCase
  applyingId: number | null
  onApply: (
    row: CrmAccountTransferCase,
    comparison: CrmPaymentComparison | undefined,
    scope: CrmAccountTransferRebuildScope | null
  ) => void
  onAction: (row: CrmAccountTransferCase, action: CrmAccountTransferAction) => void
  onComplete: (row: CrmAccountTransferCase) => void
  onRebuild: (row: CrmAccountTransferCase, scope: CrmAccountTransferRebuildScope | null) => void
}) {
  const busy = applyingId === row.id
  const isOpen = ACTIVE_STATUSES.has(row.status)
  const canApply = Boolean(row.proposed_account) && isOpen && !row.applied_at && row.status !== 'verified'
  // Проверено = факт, а не отдельная видимая ступень UI: основной путь после проведения
  // завершает кейс одной кнопкой, а сервер проверяет наличие свежей сверки.
  const canVerify = isOpen && row.status !== 'verified' && Boolean(row.applied_at)
  const canClose = row.status === 'verified' || row.status === 'dismissed'
  const [comparisonOpen, setComparisonOpen] = useState(false)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [comparisonError, setComparisonError] = useState<string | null>(null)
  const [comparison, setComparison] = useState<CrmPaymentComparison | undefined>()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [scope, setScope] = useState<CrmAccountTransferRebuildScope | null>(null)
  const [scopeLoading, setScopeLoading] = useState(false)
  const [scopeError, setScopeError] = useState<string | null>(null)
  const canRebuild = Boolean(row.applied_at)
  const showMenu = (isOpen && row.status !== 'verified') || canRebuild
  // Диапазон показываем ДО нажатия «Провести», а не в момент действия: раньше
  // оператор вообще не видел, что часть перехода останется непересобранной.
  // Только для кейсов в работе — иначе список завершённых давал бы по запросу
  // на строку; кнопка пересборки работает и без предзагруженного диапазона.
  const scopeNeeded = isOpen && (canApply || canRebuild)

  useEffect(() => {
    if (!scopeNeeded) {
      setScope(null)
      return
    }

    let cancelled = false
    setScopeLoading(true)
    setScopeError(null)
    fetchCrmAccountTransferRebuildScope(row.id)
      .then(result => {
        if (!cancelled) setScope(result)
      })
      .catch((error: unknown) => {
        if (!cancelled) setScopeError(errorMessage(error, 'Не удалось рассчитать диапазон пересборки'))
      })
      .finally(() => {
        if (!cancelled) setScopeLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [row.id, row.applied_at, scopeNeeded])

  async function captureComparisons() {
    setComparisonLoading(true)
    setComparisonError(null)
    try {
      const freshComparison = await captureCrmPaymentComparison(row.id)
      setComparison(freshComparison)
    } catch (error: unknown) {
      setComparisonError(errorMessage(error, 'Не удалось сверить платежи'))
    } finally {
      setComparisonLoading(false)
    }
  }

  async function toggleComparison() {
    if (comparisonOpen) {
      setComparisonOpen(false)
      return
    }

    setComparisonOpen(true)
    if (comparison) return

    setComparisonLoading(true)
    setComparisonError(null)
    try {
      const latestComparison = await fetchLatestCrmPaymentComparison(row.id)

      if (latestComparison) {
        setComparison(latestComparison)
      } else {
        const freshComparison = await captureCrmPaymentComparison(row.id)
        setComparison(freshComparison)
      }
    } catch (error: unknown) {
      setComparisonError(errorMessage(error, 'Не удалось загрузить сверку платежей'))
    } finally {
      setComparisonLoading(false)
    }
  }

  function requestApply() {
    const comparisonRequired =
      row.transfer_model !== 'same_account_rename' && row.old_account !== row.proposed_account

    if (comparisonRequired && !comparison) {
      window.alert('Сначала выполните сверку платежей за 35 дней.')
      setComparisonOpen(true)
      if (!comparisonLoading) void captureComparisons()
      return
    }

    onApply(row, comparison, scope)
  }

  return (
    <div className="border-b border-border px-3 py-3 last:border-b-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              #{row.studio_number} {row.city_name ?? 'Без города'}
            </span>
            <span className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${statusTone(row.status)}`}>
              {STATUS_LABELS[row.status] ?? row.status}
            </span>
            {row.suspicious_studio_number !== null && (
              <span className="rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[11px] font-medium text-orange-700">
                номер {row.suspicious_studio_number}
              </span>
            )}
          </div>

          <div className="mt-1 text-xs text-muted-foreground">
            Переход: {formatDate(row.transition_date)} · {row.address ?? 'адрес не указан'}
            {row.transfer_model ? ` · ${TRANSFER_MODEL_LABELS[row.transfer_model]}` : ''}
          </div>

          <div className="mt-2 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
            <div className="rounded border border-border bg-background px-2 py-1.5">
              <div className="text-muted-foreground">До перехода</div>
              <div className="font-medium text-foreground">{accountLabel(row.old_account)}</div>
              <div className="truncate text-muted-foreground">
                {partnerLabel(row.old_persons_fio, row.old_partner_number)}
              </div>
            </div>
            <div className="rounded border border-border bg-background px-2 py-1.5">
              <div className="text-muted-foreground">После перехода</div>
              <div className="font-medium text-foreground">{accountLabel(row.proposed_account)}</div>
              <div className="truncate text-muted-foreground">
                {partnerLabel(row.new_persons_fio, row.new_partner_number)}
              </div>
            </div>
          </div>

          {scopeNeeded && (
            <RebuildScopeNote scope={scope} loading={scopeLoading} error={scopeError} />
          )}

          {row.operator_note && (
            <div className="mt-2 text-xs text-muted-foreground">{row.operator_note}</div>
          )}
        </div>

        <div className="flex shrink-0 items-start gap-1.5">
          <CaseActionButton
            onClick={() => void toggleComparison()}
            disabled={comparisonLoading}
            icon={
              comparisonLoading ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : comparisonOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )
            }
          >
            Сверка
          </CaseActionButton>
          {canApply && (
            <CaseActionButton
              tone="primary"
              onClick={requestApply}
              disabled={busy}
              title="Зафиксировать старый аккаунт до даты перехода и новый аккаунт с даты перехода"
              icon={busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            >
              Провести
            </CaseActionButton>
          )}
          {canVerify && (
            <CaseActionButton
              tone="primary"
              onClick={() => onComplete(row)}
              disabled={busy}
              title="Отметить проверенным и убрать кейс из рабочей очереди"
              icon={busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            >
              Завершить
            </CaseActionButton>
          )}
          {!canApply && !canVerify && canClose && (
            <CaseActionButton
              tone="primary"
              onClick={() => onAction(row, 'close')}
              disabled={busy}
              icon={busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
            >
              Закрыть
            </CaseActionButton>
          )}
          {showMenu && (
            <div className="relative">
              <CaseActionButton
                aria-expanded={actionsOpen}
                aria-label="Дополнительные действия"
                onClick={() => setActionsOpen(value => !value)}
                disabled={busy}
                icon={<MoreHorizontal className="h-3.5 w-3.5" />}
              >
                Ещё
              </CaseActionButton>
              {actionsOpen && (
                <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border border-border bg-card p-1 shadow-lg">
                  {isOpen && row.status !== 'verified' && (
                    <CaseActionButton
                      tone="menu"
                      onClick={() => {
                        setActionsOpen(false)
                        onAction(row, 'postpone')
                      }}
                      icon={<Clock className="h-3.5 w-3.5" />}
                    >
                      Отложить
                    </CaseActionButton>
                  )}
                  {isOpen && row.status !== 'verified' && (
                    <CaseActionButton
                      tone="menu"
                      onClick={() => {
                        setActionsOpen(false)
                        onAction(row, 'dismiss')
                      }}
                      icon={<Ban className="h-3.5 w-3.5" />}
                    >
                      Не актуально
                    </CaseActionButton>
                  )}
                  {canRebuild && (
                    <CaseActionButton
                      tone="menu"
                      onClick={() => {
                        setActionsOpen(false)
                        onRebuild(row, scope)
                      }}
                      title="Пересобрать выручку за полное окно перехода — для кейсов, проведённых прежним 35-дневным окном"
                      icon={<ServerCog className="h-3.5 w-3.5" />}
                    >
                      Пересобрать выручку
                    </CaseActionButton>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {comparisonOpen && (
        <PaymentComparisonPanel
          comparison={comparison}
          loading={comparisonLoading}
          error={comparisonError}
          onRefresh={() => void captureComparisons()}
        />
      )}
    </div>
  )
}

type PageTab = 'accounts' | 'cases'

const PAGE_TABS: Array<{ value: PageTab; label: string }> = [
  { value: 'accounts', label: 'Аккаунты' },
  { value: 'cases', label: 'Кейсы переноса' },
]

export function CrmAccountTransfersPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore(s => s.user)
  // Deep-link `/management/crm-accounts?account=an7221` ведёт на мастер-экран
  // источников: с платёжного экрана и из Telegram-алерта приходят именно по
  // аккаунту, а не по кейсу переноса.
  const deepLinkAccount = searchParams.get('account')
  const deepLinkIncident = searchParams.get('incident')
  // Мастер-экран источников — основное назначение страницы, поэтому «Аккаунты»
  // открываются первыми; кейсы переноса остаются второй вкладкой.
  const [tab, setTab] = useState<PageTab>('accounts')
  const [rows, setRows] = useState<CrmAccountTransferCase[]>([])
  const [caseFilter, setCaseFilter] = useState<CaseListFilter>('active')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [applyingId, setApplyingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [manualForm, setManualForm] = useState<ManualFormState>(() => emptyManualForm())
  const [studioOptions, setStudioOptions] = useState<CrmAccountTransferStudioOption[]>([])
  const [partnerOptions, setPartnerOptions] = useState<CrmAccountTransferPartnerOption[]>([])
  const [formOptionsLoading, setFormOptionsLoading] = useState(false)
  const [studioQuery, setStudioQuery] = useState('')
  const [oldPartnerQuery, setOldPartnerQuery] = useState('')
  const [newPartnerQuery, setNewPartnerQuery] = useState('')
  const [registryOpen, setRegistryOpen] = useState(false)
  const [registrySearch, setRegistrySearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Параметр одноразовый: панель уже подставила аккаунт в поиск и раскрыла
  // карточку, дальше он только мешал бы менять фильтры.
  const consumeDeepLink = useCallback(() => {
    setSearchParams(
      params => {
        const next = new URLSearchParams(params)
        next.delete('account')
        next.delete('incident')
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  async function load() {
    if (!user?.id) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchCrmAccountTransferCases(null)
      setRows(data)
    } catch (error: unknown) {
      setError(errorMessage(error, 'Не удалось загрузить кейсы CRM'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || (!manualOpen && !registryOpen) || !manualForm.transitionDate) return

    let cancelled = false
    setFormOptionsLoading(true)
    Promise.all([
      fetchCrmAccountTransferStudioOptions(manualForm.transitionDate),
      partnerOptions.length > 0
        ? Promise.resolve(partnerOptions)
        : fetchCrmAccountTransferPartnerOptions(),
    ])
      .then(([studios, partners]) => {
        if (cancelled) return
        setStudioOptions(studios)
        setPartnerOptions(partners)
        setManualForm(form => {
          const selected = studios.find(row => String(row.studio_internal_id) === form.studioInternalId)
          if (!selected) return form
          const oldAccount = selected.old_account ?? selected.current_account ?? ''
          return {
            ...form,
            oldAccount,
            proposedAccount: form.transferModel === 'same_account_rename' ? oldAccount : form.proposedAccount,
            oldPartnerNumber: selected.old_partner_number == null ? '' : String(selected.old_partner_number),
            newPartnerNumber: selected.new_partner_number == null ? '' : String(selected.new_partner_number),
          }
        })
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить реестр CRM-аккаунтов'))
      })
      .finally(() => {
        if (!cancelled) setFormOptionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [user?.id, manualOpen, registryOpen, manualForm.transitionDate])

  const filteredRows = useMemo(
    () => rows.filter(row => matchesCaseListFilter(row, caseFilter) && matchesSearch(row, search)),
    [rows, caseFilter, search]
  )

  const filteredStudioOptions = useMemo(() => {
    const q = studioQuery.trim().toLowerCase().replace(/ё/g, 'е')
    const options = q
      ? studioOptions.filter(row => {
          const haystack = [
            row.studio_number,
            row.city_name,
            row.address,
            row.current_account,
            row.old_account,
            row.old_partner_number,
            row.old_persons_fio,
            row.new_partner_number,
            row.new_persons_fio,
          ].filter(value => value !== null && value !== undefined).join(' ').toLowerCase().replace(/ё/g, 'е')
          return haystack.includes(q)
        })
      : studioOptions
    return options.slice(0, 60)
  }, [studioOptions, studioQuery])

  const registryRows = useMemo(() => {
    const q = registrySearch.trim().toLowerCase().replace(/ё/g, 'е')
    const options = q
      ? studioOptions.filter(row => studioOptionLabel(row).toLowerCase().replace(/ё/g, 'е').includes(q))
      : studioOptions
    return options.slice(0, 100)
  }, [studioOptions, registrySearch])

  const selectedStudio = useMemo(
    () => studioOptions.find(row => String(row.studio_internal_id) === manualForm.studioInternalId) ?? null,
    [studioOptions, manualForm.studioInternalId]
  )

  const stats = useMemo(() => {
    const active = rows.filter(row => ACTIVE_STATUSES.has(row.status)).length
    const needsCrm = rows.filter(row => row.status === 'needs_crm_action').length
    const needsConfirmation = rows.filter(row => row.status === 'needs_confirmation').length
    return { active, needsCrm, needsConfirmation }
  }, [rows])

  async function handleApply(
    row: CrmAccountTransferCase,
    comparison: CrmPaymentComparison | undefined,
    scope: CrmAccountTransferRebuildScope | null
  ) {
    if (!row.proposed_account) return

    const comparisonLine = comparison
      ? `\n\nСверка за 35 дней: ${COMPARISON_LABELS[comparison.status]}.\n${comparisonTotalsWarning(comparison)}`
      : ''
    // Диапазон повторяем в подтверждении тем же текстом, что и в карточке:
    // источник один — серверный scope, который Apply и пересоберёт.
    const scopeLine = scope ? `\n\n${rebuildScopeLines(scope).join('\n')}` : ''
    // После проведения сервер сразу пересобирает окно перехода по историчной карте:
    // до D учитывается старый аккаунт, с D — новый. Сверка здесь только объясняет,
    // какие строки будут исключены, а не требует совпадения двух CRM-историй.
    const bookedLine = comparison?.has_booked_duplicate
      ? [
          '',
          `Сейчас в выручке учтены оба набора — ${formatAmount(
            comparison.ledger_total,
            comparison.currencies[0]?.currency ?? 'RUB'
          )}.`,
          'После проведения выручка будет сразу пересобрана за окно перехода.',
        ].join('\n')
      : ''
    const riskLine = comparison && comparisonHasHistoryRisk(comparison)
      ? [
          '',
          'Платежи аккаунтов различаются — при переносе истории это допустимо.',
          `До ${formatDate(row.transition_date)} будет сохранён старый аккаунт, с этой даты — новый. Суммы между аккаунтами не объединяются и не обязаны совпадать.`,
        ].join('\n')
      : ''
    const confirmed = window.confirm(
      `Провести переход студии #${row.studio_number} на аккаунт ${row.proposed_account} с ${formatDate(row.transition_date)}?${scopeLine}${comparisonLine}${bookedLine}${riskLine}`
    )
    if (!confirmed) return

    setApplyingId(row.id)
    setError(null)
    setSuccess(null)
    try {
      const result = await applyCrmAccountTransferCase({
        caseId: row.id,
        confirmCrmAction: false,
      })
      const window_ = scope?.has_window
        ? `выручка пересобрана за ${formatDate(scope.window_start)} — ${formatDate(scope.window_end)}`
        : 'выручка пересобрана за окно перехода'
      setSuccess(
        `Кейс #${result.case_id} проведён: старая история зафиксирована, ${window_}. Статус: ${STATUS_LABELS[result.status] ?? result.status}`
      )
      await load()
    } catch (error: unknown) {
      setError(errorMessage(error, 'Не удалось провести кейс'))
    } finally {
      setApplyingId(null)
    }
  }

  async function handleRebuild(
    row: CrmAccountTransferCase,
    scope: CrmAccountTransferRebuildScope | null
  ) {
    const scopeLine = scope ? `\n\n${rebuildScopeLines(scope).join('\n')}` : ''
    const confirmed = window.confirm(
      `Пересобрать выручку по кейсу #${row.id} (студия #${row.studio_number})?${scopeLine}\n\nПересборка идёт по текущей CRM-карте и не меняет сам кейс.`
    )
    if (!confirmed) return

    setApplyingId(row.id)
    setError(null)
    setSuccess(null)
    try {
      const result = await rebuildCrmAccountTransferCaseWindow(row.id)
      setSuccess(
        `Кейс #${result.case_id}: выручка пересобрана за ${formatDate(result.window_start)} — ${formatDate(
          result.window_end
        )} (дней ${result.refresh.rebuilt_dates_count}, признак чужого аккаунта изменён у ${
          result.refresh.changed_count
        } строк).`
      )
      await load()
    } catch (error: unknown) {
      setError(errorMessage(error, 'Не удалось пересобрать выручку'))
    } finally {
      setApplyingId(null)
    }
  }

  async function handleCaseAction(row: CrmAccountTransferCase, action: CrmAccountTransferAction) {
    let note: string | null = null
    let postponedUntil: string | null = null

    if (action === 'postpone') {
      const suggested = new Date()
      suggested.setDate(suggested.getDate() + 7)
      const answer = window.prompt(
        `До какой даты отложить кейс #${row.id} (ГГГГ-ММ-ДД)?`,
        suggested.toISOString().slice(0, 10)
      )
      if (!answer) return
      if (!/^\d{4}-\d{2}-\d{2}$/.test(answer.trim())) {
        setError('Дата должна быть в формате ГГГГ-ММ-ДД')
        return
      }
      postponedUntil = answer.trim()
      note = window.prompt('Комментарий (необязательно)') ?? null
    }

    if (action === 'dismiss') {
      const answer = window.prompt(
        `Почему кейс #${row.id} не актуален? Причина обязательна.`
      )
      if (!answer || !answer.trim()) return
      note = answer
    }

    if (action === 'mark_verified') {
      const confirmed = window.confirm(
        [
          `Отметить кейс #${row.id} проверенным?`,
          '',
          'Перед этим откройте «Сверка» и нажмите «Обновить».',
          'Проверьте, что до даты перехода используется старый аккаунт, а с даты перехода — новый.',
        ].join('\n')
      )
      if (!confirmed) return
      note = window.prompt('Комментарий к проверке (необязательно)') ?? null
    }

    if (action === 'close') {
      const confirmed = window.confirm(`Закрыть кейс #${row.id}? Он уйдёт из рабочей очереди.`)
      if (!confirmed) return
      note = window.prompt('Комментарий (необязательно)') ?? null
    }

    setApplyingId(row.id)
    setError(null)
    setSuccess(null)
    try {
      const result = await updateCrmAccountTransferCaseStatus({
        caseId: row.id,
        action,
        note,
        postponedUntil,
      })
      setSuccess(
        `Кейс #${result.case_id}: ${STATUS_LABELS[result.previous_status] ?? result.previous_status} → ${
          STATUS_LABELS[result.status] ?? result.status
        }`
      )
      await load()
    } catch (actionError: unknown) {
      setError(errorMessage(actionError, 'Не удалось изменить статус кейса'))
    } finally {
      setApplyingId(null)
    }
  }

  async function handleCompleteCase(row: CrmAccountTransferCase) {
    const confirmed = window.confirm(
      [
        `Завершить кейс #${row.id}?`,
        '',
        'Перед завершением проверьте обновлённую «Сверку».',
        'В выручке должны остаться правильные платежи: до даты перехода старый аккаунт, с даты перехода — новый.',
      ].join('\n')
    )
    if (!confirmed) return
    const note = window.prompt('Комментарий к завершению (необязательно)') ?? null

    setApplyingId(row.id)
    setError(null)
    setSuccess(null)
    try {
      const verified = await updateCrmAccountTransferCaseStatus({
        caseId: row.id,
        action: 'mark_verified',
        note,
      })
      const closed = await updateCrmAccountTransferCaseStatus({
        caseId: row.id,
        action: 'close',
        note,
      })
      setSuccess(
        `Кейс #${closed.case_id} завершён: ${
          STATUS_LABELS[verified.previous_status] ?? verified.previous_status
        } → ${STATUS_LABELS[closed.status] ?? closed.status}`
      )
      await load()
    } catch (actionError: unknown) {
      setError(errorMessage(actionError, 'Не удалось завершить кейс'))
    } finally {
      setApplyingId(null)
    }
  }

  function selectStudio(studioInternalId: string) {
    const selected = studioOptions.find(row => String(row.studio_internal_id) === studioInternalId)
    setManualForm(form => {
      if (!selected) {
        return {
          ...form,
          studioInternalId: '',
          oldAccount: '',
          oldPartnerNumber: '',
          newPartnerNumber: '',
        }
      }

      const oldAccount = selected.old_account ?? selected.current_account ?? ''
      return {
        ...form,
        studioInternalId,
        oldAccount,
        proposedAccount: form.transferModel === 'same_account_rename' ? oldAccount : '',
        oldPartnerNumber: selected.old_partner_number == null ? '' : String(selected.old_partner_number),
        newPartnerNumber: selected.new_partner_number == null ? '' : String(selected.new_partner_number),
      }
    })
    setOldPartnerQuery('')
    setNewPartnerQuery('')
  }

  function selectTransferModel(transferModel: CrmAccountTransferModel) {
    setManualForm(form => ({
      ...form,
      transferModel,
      proposedAccount: transferModel === 'same_account_rename' ? form.oldAccount : '',
    }))
  }

  async function handleCreateManual(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const studioInternalId = parseOptionalNumber(manualForm.studioInternalId)
    const oldPartnerNumber = parseOptionalNumber(manualForm.oldPartnerNumber)
    const newPartnerNumber = parseOptionalNumber(manualForm.newPartnerNumber)

    if (!studioInternalId || !manualForm.transitionDate || !manualForm.proposedAccount.trim()) {
      setError('Выберите студию, дату перехода и CRM-аккаунт после перехода')
      return
    }

    setCreating(true)
    setError(null)
    setSuccess(null)
    try {
      const caseId = await createCrmAccountTransferCase({
        studioInternalId,
        transitionDate: manualForm.transitionDate,
        detectionKind: 'manual',
        transferModel: manualForm.transferModel,
        oldAccount: manualForm.oldAccount,
        proposedAccount: manualForm.proposedAccount,
        oldPartnerNumber,
        newPartnerNumber,
        evidence: {
          source: 'management_ui',
          operator_note: manualForm.note.trim() || null,
        },
        status: 'needs_crm_action',
      })
      setSuccess(`Кейс #${caseId} создан`)
      setManualForm(emptyManualForm())
      setManualOpen(false)
      await load()
    } catch (error: unknown) {
      setError(errorMessage(error, 'Не удалось создать кейс'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate('/management/hub')}
            className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Направления
          </button>
          <div className="flex items-center gap-2">
            <ServerCog className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">CRM-аккаунты</h1>
          </div>
        </div>
        {tab === 'cases' && (
          <div className="flex shrink-0 gap-2">
            <CaseActionButton
              onClick={() => setManualOpen(value => !value)}
              icon={manualOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            >
              Новый кейс
            </CaseActionButton>
            <CaseActionButton
              onClick={() => void load()}
              disabled={loading}
              icon={<RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />}
            >
              Обновить
            </CaseActionButton>
          </div>
        )}
      </div>

      <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
        {PAGE_TABS.map(item => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={cn(
              'h-8 whitespace-nowrap rounded px-3 text-xs font-medium transition-colors',
              tab === item.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'accounts' && (
        <CrmSourceAccountsPanel
          deepLinkAccount={deepLinkAccount}
          deepLinkIncident={deepLinkIncident}
          onDeepLinkConsumed={consumeDeepLink}
        />
      )}

      {tab === 'cases' && (
      <>
      {manualOpen && (
        <Card className="p-3">
          <form onSubmit={handleCreateManual} className="space-y-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="text-xs font-medium text-muted-foreground">
                Дата перехода
                <input
                  type="date"
                  value={manualForm.transitionDate}
                  onChange={event => setManualForm(form => ({ ...form, transitionDate: event.target.value }))}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
                />
              </label>
              <label className="text-xs font-medium text-muted-foreground">
                Что меняется в CRM
                <select
                  value={manualForm.transferModel}
                  onChange={event => selectTransferModel(event.target.value as CrmAccountTransferModel)}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
                >
                  <option value="new_account">Переход на другой CRM-аккаунт</option>
                  <option value="same_account_rename">Смена владельца в текущем CRM-аккаунте</option>
                </select>
              </label>
            </div>

            <label className="block text-xs font-medium text-muted-foreground">
              Студия
              <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                <Search className="h-3.5 w-3.5 shrink-0" />
                <input
                  value={studioQuery}
                  onChange={event => setStudioQuery(event.target.value)}
                  placeholder="номер студии, город, адрес, CRM-аккаунт или франчайзи"
                  className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                />
                {formOptionsLoading && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
              </div>
              <select
                value={manualForm.studioInternalId}
                onChange={event => selectStudio(event.target.value)}
                size={6}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                <option value="">Выберите студию</option>
                {filteredStudioOptions.map(row => (
                  <option key={row.studio_internal_id} value={row.studio_internal_id}>
                    {studioOptionLabel(row)}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                Показано {filteredStudioOptions.length} из {studioOptions.length}. Технический internal_id вводить не нужно.
              </span>
            </label>

            {selectedStudio && (
              <div className="rounded border border-border bg-muted/20 px-3 py-2 text-xs">
                <div className="font-semibold text-foreground">{studioOptionLabel(selectedStudio)}</div>
                <div className="mt-1 text-muted-foreground">
                  Старый аккаунт берётся из реестра на {formatDate(previousDate(manualForm.transitionDate))}.
                  Франчайзи подставлены из истории владения до и после перехода.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="text-xs font-medium text-muted-foreground">
                CRM-аккаунт до перехода
                <input
                  value={manualForm.oldAccount}
                  readOnly
                  placeholder="В реестре нет привязки"
                  className="mt-1 w-full rounded border border-border bg-muted/30 px-2 py-2 text-sm text-foreground outline-none"
                />
                <span className="mt-1 block text-[11px] font-normal">Из реестра «студия → аккаунт»</span>
              </label>
              <label className="text-xs font-medium text-muted-foreground">
                CRM-аккаунт после перехода
                <input
                  value={manualForm.proposedAccount}
                  onChange={event => setManualForm(form => ({ ...form, proposedAccount: event.target.value }))}
                  readOnly={manualForm.transferModel === 'same_account_rename'}
                  placeholder={manualForm.transferModel === 'new_account' ? 'например, an10932' : 'Подставится текущий аккаунт'}
                  className={`mt-1 w-full rounded border border-border px-2 py-2 text-sm text-foreground outline-none focus:border-primary ${
                    manualForm.transferModel === 'same_account_rename' ? 'bg-muted/30' : 'bg-background'
                  }`}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <SearchablePartnerSelect
                label="Франчайзи до перехода"
                value={manualForm.oldPartnerNumber}
                query={oldPartnerQuery}
                options={partnerOptions}
                onQueryChange={setOldPartnerQuery}
                onChange={value => setManualForm(form => ({ ...form, oldPartnerNumber: value }))}
              />
              <SearchablePartnerSelect
                label="Франчайзи после перехода"
                value={manualForm.newPartnerNumber}
                query={newPartnerQuery}
                options={partnerOptions}
                onQueryChange={setNewPartnerQuery}
                onChange={value => setManualForm(form => ({ ...form, newPartnerNumber: value }))}
              />
            </div>

            <label className="block text-xs font-medium text-muted-foreground">
              Заметка
              <textarea
                value={manualForm.note}
                onChange={event => setManualForm(form => ({ ...form, note: event.target.value }))}
                rows={2}
                className="mt-1 w-full resize-none rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
              />
            </label>

            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setManualOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" size="sm" disabled={creating}>
                {creating ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                Создать
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Активные кейсы</div>
          <div className="text-xl font-semibold">{stats.active}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Ждут CRM</div>
          <div className="text-xl font-semibold">{stats.needsCrm}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">На проверке</div>
          <div className="text-xl font-semibold">{stats.needsConfirmation}</div>
        </Card>
      </div>

      <Card className="overflow-hidden p-0">
        <button
          type="button"
          onClick={() => setRegistryOpen(value => !value)}
          className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
        >
          <div>
            <div className="text-sm font-semibold">Реестр «студия → CRM-аккаунт»</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Текущая привязка; в кейсе старый аккаунт берётся из этого реестра на день перед переходом
            </div>
          </div>
          {registryOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {registryOpen && (
          <div className="border-t border-border p-3">
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={registrySearch}
                onChange={event => setRegistrySearch(event.target.value)}
                placeholder="Номер студии, город, адрес или CRM-аккаунт"
                className="w-full rounded border border-border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>

            {formOptionsLoading && studioOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Загружаем реестр…</div>
            ) : (
              <div className="overflow-x-auto rounded border border-border">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 font-medium">Студия</th>
                      <th className="px-2 py-2 font-medium">Город и адрес</th>
                      <th className="px-2 py-2 font-medium">Текущий CRM-аккаунт</th>
                      <th className="px-2 py-2 font-medium">Действует с</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registryRows.map(row => (
                      <tr key={row.studio_internal_id} className="border-t border-border">
                        <td className="whitespace-nowrap px-2 py-2 font-medium">#{row.studio_number}</td>
                        <td className="px-2 py-2">
                          {[row.city_name, row.address].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {row.current_account || <span className="text-amber-700">Не привязан</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                          {row.current_account_valid_from ? formatDate(row.current_account_valid_from) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {registryRows.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">Ничего не найдено</div>
                )}
              </div>
            )}
            <div className="mt-2 text-[11px] text-muted-foreground">
              Показано {registryRows.length} из {studioOptions.length}. Реестр хранит историю привязок по датам.
            </div>
          </div>
        )}
      </Card>

      <Card className="p-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Поиск по студии, аккаунту, франчайзи"
              className="w-full rounded border border-border bg-background py-2 pl-8 pr-3 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="inline-flex shrink-0 rounded-md border border-border bg-muted/30 p-0.5">
            {CASE_LIST_FILTERS.map(filter => {
              const selected = caseFilter === filter.value
              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setCaseFilter(filter.value)}
                  className={cn(
                    'h-7 whitespace-nowrap rounded px-2.5 text-xs font-medium transition-colors',
                    selected
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5 p-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {success && (
        <Card className="border-emerald-500/40 bg-emerald-500/5 p-3">
          <div className="flex items-start gap-2 text-sm text-emerald-700">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{success}</span>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div className="text-sm font-semibold">Кейсы</div>
          <Badge>{filteredRows.length}</Badge>
        </div>
        {loading ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">Загрузка...</div>
        ) : filteredRows.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground">Нет кейсов</div>
        ) : (
          filteredRows.map(row => (
            <CaseRow
              key={row.id}
              row={row}
              applyingId={applyingId}
              onApply={handleApply}
              onAction={handleCaseAction}
              onComplete={handleCompleteCase}
              onRebuild={handleRebuild}
            />
          ))
        )}
      </Card>
      </>
      )}
    </div>
  )
}
